import { useCallback, useRef, useState, type ChangeEvent } from 'react'
import { Button, LoadingState, Popover, Tooltip } from '@highlight/ui'
import { Clock, Plus, X } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import type { ReminderItem } from '../../../hooks/use-reminders'
import { useReminderActions } from '../../../hooks/use-reminders'
import { ReminderDatePicker } from '../date-picker'
import { ReminderSource } from '../reminder-source'
import { useAttachImagesToReminder } from '../utils/image-attachments'
import { formatDatePill, formatDateTooltip } from '../utils/due-date'
import { useDueSoon } from '../hooks/use-due-soon'
import { usePreferredHourCycle } from '../utils/user-time-format'

/**
 * Origin attribution ("<source> · <time>") plus hover-reveal quick actions
 * (attach image, set due date). Owns its own file input.
 */
export function ReminderSourceRow({
  item,
  resolved,
  isSaving,
  onSaving,
  offersMoves = true,
}: {
  item: ReminderItem
  resolved: boolean
  isSaving: boolean
  onSaving: () => void
  /**
   * The quick actions. Off where the host carries the same moves itself — the
   * Reminder View puts the due date in its own footer, so a second clock here
   * would be a duplicate stranded on a line of its own.
   */
  offersMoves?: boolean
}) {
  const { updateItem } = useReminderActions()
  const attachImages = useAttachImagesToReminder(item.id, onSaving)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [clockPickerOpen, setClockPickerOpen] = useState(false)
  const hourCycle = usePreferredHourCycle()
  const dueSoon = useDueSoon(item.scheduledAt)

  const handleFileInputChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const list = e.target.files
      if (list?.length) {
        await attachImages(Array.from(list))
      }
      e.target.value = ''
    },
    [attachImages],
  )

  return (
    <div className="flex w-full items-center gap-2 pl-7">
      {item.source && (
        <div className="min-w-0 flex-1 overflow-hidden">
          <ReminderSource item={item} />
        </div>
      )}

      {isSaving && <LoadingState.Spinner className="size-3 shrink-0" />}

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="image/*"
        multiple
        onChange={handleFileInputChange}
      />

      {offersMoves && (
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <Tooltip.Provider>
            {/* Plus button — hover only */}
            <div
              className={twMerge(
                'flex shrink-0 transition-opacity duration-150',
                'pointer-events-none opacity-0',
                'group-hover/cell:pointer-events-auto group-hover/cell:opacity-100',
                'group-focus-within/cell:pointer-events-auto group-focus-within/cell:opacity-100',
              )}
            >
              <Tooltip>
                <Tooltip.Trigger
                  render={
                    <Button
                      variant="outline"
                      shape="square"
                      aria-label="Add image"
                      onClick={(e) => {
                        e.stopPropagation()
                        fileInputRef.current?.click()
                      }}
                    >
                      <Plus />
                    </Button>
                  }
                />
                <Tooltip.Content>Add image</Tooltip.Content>
              </Tooltip>
            </div>

            {/* Date pill — always visible when scheduled, hover-only when not */}
            <Popover
              open={clockPickerOpen}
              onOpenChange={(open) => {
                setClockPickerOpen(open)
                if (open) {
                  AmplitudeRenderer.trackEvent('Reminder Action', {
                    type: 'Open Date Picker',
                    source: 'footer',
                  })
                }
              }}
            >
              <div
                className={twMerge(
                  'flex shrink-0 transition-opacity duration-150',
                  !item.scheduledAt && 'pointer-events-none opacity-0',
                  !item.scheduledAt && 'group-hover/cell:pointer-events-auto group-hover/cell:opacity-100',
                  !item.scheduledAt &&
                    'group-focus-within/cell:pointer-events-auto group-focus-within/cell:opacity-100',
                )}
              >
                <Tooltip>
                  <Tooltip.Trigger
                    render={
                      <Popover.Trigger
                        render={
                          <Button
                            variant="outline"
                            tone={!resolved && dueSoon ? 'danger' : undefined}
                            shape={item.scheduledAt ? undefined : 'square'}
                            aria-label="Set due date and time"
                            onClick={(e) => e.stopPropagation()}
                          />
                        }
                      />
                    }
                  >
                    {item.scheduledAt ? (
                      <span className="group/date flex items-center gap-1">
                        <span className="group-hover/date:hidden">
                          <Clock className="size-3" strokeWidth={2} />
                        </span>
                        <span
                          className="hidden group-hover/date:block"
                          onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            updateItem(item.id, item.title, null)
                            onSaving()
                            setClockPickerOpen(false)
                          }}
                        >
                          <X className="size-3" strokeWidth={2} />
                        </span>
                        <span className="font-medium leading-none">{formatDatePill(item.scheduledAt, hourCycle)}</span>
                      </span>
                    ) : (
                      <Clock className="size-3" strokeWidth={2} />
                    )}
                  </Tooltip.Trigger>
                  <Tooltip.Content>
                    {item.scheduledAt ? formatDateTooltip(item.scheduledAt, hourCycle) : 'Set due date'}
                  </Tooltip.Content>
                </Tooltip>
              </div>
              <Popover.Content side="bottom" align="end" sideOffset={8} initialFocus={false} className="w-[320px] p-1">
                <ReminderDatePicker
                  value={item.scheduledAt}
                  onChange={(date) => {
                    updateItem(item.id, item.title, date)
                    onSaving()
                  }}
                />
              </Popover.Content>
            </Popover>
          </Tooltip.Provider>
        </div>
      )}
    </div>
  )
}
